const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const todayMid = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };

const IP_GRANTED_TIPS = new Set([
  'marca_osim', 'marca_euipo', 'marca_wipo', 'marca_internationala', 'dmi', 'idepot', 'brevet',
]);

/* True for an IP title that is already granted — a registration date or an
   explicit granted flag on file. The intermediate timeline (publicare,
   opozitie, examinare...) no longer applies.
   Note: an expiry date alone is NOT enough — bulk-imported pending applications
   also carry a computed expiry (filing date + 10y). */
export function isProcGranted(tip, data = {}) {
  if (!IP_GRANTED_TIPS.has(tip)) return false;
  const r = data;
  return !!(r.marcaInregistrata || r.brevetPrimit || r.dataInregistrare);
}

/* Returns { label, sev } where sev = 'neutral'|'info'|'watch'|'act'|'overdue'|'done' */
export function getProcStatus(tip, data = {}) {
  const r = data;

  if (isProcGranted(tip, data)) {
    if (r.certificatPrimit && !r.transmisClient) return { label: 'De transmis client', sev: 'act' };
    return { label: tip === 'brevet' ? 'In vigoare' : 'Inregistrata', sev: 'done' };
  }

  const finishedIf = (cond, label = 'Procedura finalizata') =>
    cond ? { label, sev: 'done' } : null;

  switch (tip) {
    case 'marca_osim': {
      const bopi = parseISO(r.decizieDataBOPI);
      const opozitieEnd = bopi ? addMonths(bopi, 2) : null;
      const opozitieEnded = opozitieEnd ? todayMid() >= opozitieEnd : false;
      if (!r.depusa)              return { label: 'In pregatire', sev: 'neutral' };
      if (!r.dataPrimaPublicare)  return { label: 'Asteptare publicare BOPI', sev: 'info' };
      if (!bopi)                  return { label: 'Asteptare decizie admitere', sev: 'info' };
      if (!opozitieEnded)         return { label: 'Perioada opozitie (2 luni)', sev: 'watch' };
      if (!r.certificatPrimit)    return { label: 'Plata certificat', sev: 'act' };
      if (!r.transmisClient)      return { label: 'De transmis client', sev: 'act' };
      return { label: 'Finalizata', sev: 'done' };
    }

    case 'marca_euipo': {
      const pub = parseISO(r.dataPublicare);
      const opozitieEnd = pub ? addMonths(pub, 3) : null;
      const opozitieEnded = opozitieEnd ? todayMid() >= opozitieEnd : false;
      if (!r.depusa)             return { label: 'In pregatire', sev: 'neutral' };
      if (!pub)                  return { label: 'Examinare / asteptare publicare', sev: 'info' };
      if (!opozitieEnded)        return { label: 'Perioada opozitie (3 luni)', sev: 'watch' };
      if (!r.dataInregistrare)   return { label: 'Confirmare inregistrare', sev: 'act' };
      if (!r.certificatPrimit)   return { label: 'Certificat disponibil', sev: 'act' };
      if (!r.transmisClient)     return { label: 'De transmis client', sev: 'act' };
      return { label: 'Finalizata', sev: 'done' };
    }

    case 'marca_wipo': {
      if (!r.depusa)                 return { label: 'In pregatire', sev: 'neutral' };
      if (!r.dataTransmisWIPO)       return { label: 'Transmitere la WIPO', sev: 'info' };
      if (!r.dataPublicareGazette)   return { label: 'Asteptare Gazette WIPO', sev: 'info' };
      if (!r.dataInregistrare)       return { label: 'Examinare tari desemnate', sev: 'watch' };
      if (!r.certificatPrimit)       return { label: 'De obtinut certificat', sev: 'act' };
      if (!r.transmisClient)         return { label: 'De transmis client', sev: 'act' };
      return { label: 'Finalizata', sev: 'done' };
    }

    case 'idepot': {
      if (!r.depusa)            return { label: 'In pregatire', sev: 'neutral' };
      if (!r.dataConfirmare)    return { label: 'Asteptare confirmare BOIP', sev: 'info' };
      if (!r.certificatPrimit)  return { label: 'De obtinut certificat', sev: 'act' };
      if (!r.transmisClient)    return { label: 'De transmis client', sev: 'act' };
      return { label: 'Finalizata', sev: 'done' };
    }

    case 'dmi': {
      const oficiu = (r.oficiu || '').toUpperCase();
      if (oficiu === 'EUIPO') {
        if (!r.depusa)              return { label: 'In pregatire', sev: 'neutral' };
        if (!r.dataExaminare)       return { label: 'Asteptare examinare', sev: 'info' };
        if (!r.dataPublicare && !r.amanarePublicare) return { label: 'Asteptare publicare', sev: 'info' };
        if (!r.dataInregistrare)    return { label: 'Asteptare inregistrare', sev: 'info' };
        if (!r.certificatPrimit)    return { label: 'De obtinut certificat', sev: 'act' };
        if (!r.transmisClient)      return { label: 'De transmis client', sev: 'act' };
        return { label: 'Finalizata', sev: 'done' };
      }
      // OSIM (default)
      if (!r.depusa)            return { label: 'In pregatire', sev: 'neutral' };
      if (!r.dataExaminare)     return { label: 'Asteptare examinare formala', sev: 'info' };
      if (!r.dataPublicare)     return { label: 'Asteptare publicare BOPI', sev: 'info' };
      if (!r.certificatPrimit)  return { label: 'De obtinut certificat', sev: 'act' };
      if (!r.transmisClient)    return { label: 'De transmis client', sev: 'act' };
      return { label: 'Finalizata', sev: 'done' };
    }

    case 'brevet': {
      const proc = (r.procedura || '').toLowerCase();
      if (proc.includes('oeb') || proc.includes('epo') || r.oficiu === 'OEB') {
        if (!r.depusa)                return { label: 'In pregatire', sev: 'neutral' };
        if (!r.taxeInitialePlatite)   return { label: 'Taxa de depunere OEB', sev: 'act' };
        if (!r.examinareCeruta)       return { label: 'Cerere de examinare', sev: 'act' };
        if (!r.taxaAcordarePlatita)   return { label: 'Taxa de acordare', sev: 'act' };
        if (!r.brevetPrimit)          return { label: 'De ridicat brevetul', sev: 'act' };
        if (!r.transmisClient)        return { label: 'De transmis client', sev: 'act' };
        return { label: 'In vigoare', sev: 'done' };
      }
      // OSIM
      if (!r.depusa)                  return { label: 'In pregatire', sev: 'neutral' };
      if (!r.taxaInregPlatita)        return { label: 'Taxa de inregistrare', sev: 'act' };
      if (!r.taxaPublicarePlatita)    return { label: 'Taxa de publicare', sev: 'act' };
      if (!r.examinareCeruta)         return { label: 'Cerere de examinare', sev: 'act' };
      if (!r.brevetPrimit)            return { label: 'De ridicat brevetul', sev: 'act' };
      if (!r.transmisClient)          return { label: 'De transmis client', sev: 'act' };
      return { label: 'In vigoare', sev: 'done' };
    }

    case 'dosar_instanta': {
      if (r.dosarInchis)  return { label: 'Dosar inchis', sev: 'done' };
      const faze = r.faze || {};
      const FAZE = [
        { cheie: 'fond',    label: 'Fond' },
        { cheie: 'apel',    label: 'Apel' },
        { cheie: 'recurs',  label: 'Recurs' },
      ];
      const activa = FAZE.find(f => faze[f.cheie] && !faze[f.cheie].solutie);
      if (activa) return { label: `Pe rol — ${activa.label}`, sev: 'info' };
      if (!r.nrDosar)  return { label: 'In pregatire', sev: 'neutral' };
      return { label: 'Pe rol', sev: 'info' };
    }

    default:
      return { label: 'In lucru', sev: 'neutral' };
  }
}

/* Returns the human-readable title/name for a procedure */
export function getProcTitle(tip, data = {}) {
  const r = data;
  switch (tip) {
    case 'marca_osim':
    case 'marca_euipo':
    case 'marca_wipo':
    case 'dmi':
      return r.denumire || '—';
    case 'idepot':
      return r.titluCreatie || r.denumire || '—';
    case 'brevet':
      return r.titluInventie || r.denumire || '—';
    case 'dosar_instanta':
      return r.denumire || r.obiect || '—';
    default:
      return r.denumire || r.titlu || '—';
  }
}

/* Returns the registration/deposit number for a procedure */
export function getProcNr(tip, data = {}) {
  const r = data;
  switch (tip) {
    case 'idepot': return r.numarDepozit || r.numar || '';
    case 'brevet': return r.numarDepozitOsim || r.numarDepozitOeb || r.numar || '';
    case 'dosar_instanta': return (r.faze && r.faze.fond && r.faze.fond.numarDosar) || '';
    default: return r.numar || '';
  }
}

/* Returns the filing date for a procedure */
export function getProcDataDepunere(tip, data = {}) {
  const r = data;
  if (tip === 'dosar_instanta') return r.dataDeschidere || '';
  return r.dataDepunere || r.data_depunere || '';
}

export const SEV_LABEL = {
  neutral: { label: 'In pregatire', color: 'var(--muted)' },
  info:    { label: 'In curs',      color: '#3b82f6' },
  watch:   { label: 'Monitorizare', color: '#f59e0b' },
  act:     { label: 'Actiune',      color: '#f97316' },
  overdue: { label: 'Depasit',      color: '#ef4444' },
  done:    { label: 'Finalizat',    color: '#22c55e' },
};
